import { useEffect, useState } from 'react';
import SiteLayout, { Stripe } from './SiteLayout';
import { links, HOME_PHOTOS } from './siteLinks';
import Markdown from './Markdown';

const QUICK_LINKS = [
  { kicker: '1936 – TODAY', title: 'Results', text: 'Every event, class and rider, with championships, records and personal bests.', cta: 'Results hub', href: links.results },
  { kicker: 'NEXT SEASON', title: 'Calendar', text: 'The coming season’s events, each with its regulations and entry details.', cta: 'See the calendar', href: links.calendar },
  { kicker: 'ENTER ONLINE', title: 'Event entry', text: 'Enter events from the calendar and pay by bank transfer.', cta: 'Enter an event', href: links.calendar },
  { kicker: 'ON EVENT DAYS', title: 'Live timing', text: 'Follow every run from the hill as it happens.', cta: 'live.nhca.co.uk', href: links.liveTiming },
];

const CLASSES = [
  { name: 'Open', text: 'The overall championship every rider is part of. The fastest riders score points.', chip: 'orange' },
  { name: 'Classic & Vintage', text: 'Bikes from the early days of motorcycling through the 1970s.', chip: 'blue' },
  { name: 'Forgotten Era', text: 'Bikes from 1970 to 1989: too new to be classics, too old to win outright.', chip: 'blue' },
  { name: 'Road Legal', text: 'Road bikes with an MOT and lights. Beginners compete on equal terms.', chip: 'orange' },
  { name: 'Junior', text: 'Riders aged 13 to 15 on 80–125cc manual-clutch bikes.', chip: 'orange' },
  { name: 'Sidecars & three-wheelers', text: 'Outfits and trikes compete alongside the solos on every hill.', chip: 'blue' },
];

function NextEventBar() {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/next-event')
      .then(res => (res.ok ? res.json() : null))
      .then(d => { if (!cancelled) setEvent(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!event || !event.date) return null;

  const today = event.daysUntil === 0;
  const date = new Date(`${event.date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  let closing = null;
  if (!today && event.closingDate && event.daysUntilClosing !== null) {
    if (event.daysUntilClosing > 1) closing = `Entries close in ${event.daysUntilClosing} days`;
    else if (event.daysUntilClosing === 1) closing = 'Entries close tomorrow';
    else if (event.daysUntilClosing === 0) closing = 'Entries close today';
    else closing = 'Entries closed';
  }
  const soon = (event.otherClosingSoon || []).filter(c => c.hill !== event.hill);

  return (
    <section className="site-next" aria-label="Next event">
      <div className="site-wrap">
        <div className="site-next-card">
          <div className="site-next-count">
            {today
              ? <span className="site-next-number">Today</span>
              : <>
                  <span className="site-next-number">{event.daysUntil}</span>
                  <span className="site-next-unit">{event.daysUntil === 1 ? 'day to go' : 'days to go'}</span>
                </>}
          </div>
          <div className="site-next-body">
            <span className="site-kicker">{today ? 'Event today' : 'Next event'}</span>
            <span className="site-next-hill">{event.displayHill || event.hill}</span>
            <span className="site-next-date">{date}</span>
            {closing && (
              <span className={`site-next-closing${event.daysUntilClosing !== null && event.daysUntilClosing <= 7 ? ' site-next-closing--soon' : ''}`}>{closing}</span>
            )}
            {soon.length > 0 && (
              <span className="site-next-soon">
                Also closing soon: {soon.map(c => `${c.hill} (${c.daysUntilClosing} ${c.daysUntilClosing === 1 ? 'day' : 'days'})`).join(', ')}
              </span>
            )}
          </div>
          <div className="site-next-actions">
            {today
              ? <a className="site-btn site-btn--orange" href={links.liveTiming}>Follow live timing</a>
              : <a className="site-btn site-btn--ink" href={links.calendar}>Calendar and entry</a>}
          </div>
        </div>
      </div>
    </section>
  );
}

function memoriesHeading(mode, isoDate) {
  if (mode === 'on-this-day') {
    const d = new Date(`${isoDate}T12:00:00`);
    return `On this day · ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`;
  }
  if (mode === 'this-week') return 'This week in NHCA history';
  return 'From the archive';
}

function MemoryTile({ photo, feature }) {
  const className = `site-memory${feature ? ' site-memory--feature' : ''}`;
  const alt = [photo.album, photo.year].filter(Boolean).join(', ');
  const body = (
    <>
      <img src={feature ? photo.preview : photo.thumbnail} alt={alt} loading="lazy" />
      <span className="site-memory-caption">
        {photo.year && !String(photo.album).includes(String(photo.year)) && (
          <span className="site-memory-year">{photo.year}</span>
        )}
        {photo.album}
      </span>
    </>
  );
  return photo.albumUrl
    ? <a className={className} href={photo.albumUrl}>{body}</a>
    : <div className={className}>{body}</div>;
}

function Memories() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/site/on-this-day')
      .then(res => (res.ok ? res.json() : null))
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Nothing to show (Immich not set up, unreachable, or no public albums):
  // leave the section out rather than show an empty box.
  if (!data || !data.photos || data.photos.length === 0) return null;

  return (
    <section className="site-memories" aria-labelledby="site-memories-title">
      <div className="site-wrap">
        <div className="site-section-head">
          <div>
            <span className="site-kicker">NHCA photos</span>
            <h2 className="site-h2" id="site-memories-title">{memoriesHeading(data.mode, data.date)}</h2>
            <p>Photos from past seasons, chosen fresh every day from the NHCA archive.</p>
          </div>
          <a className="site-btn site-btn--ink" href={links.photos}>Browse all photos</a>
        </div>
        <div className="site-memory-grid">
          {data.photos.map((p, i) => <MemoryTile key={p.id} photo={p} feature={i === 0} />)}
        </div>
      </div>
    </section>
  );
}

// One of the Results Hub's event videos (Web Admin > Event videos), a
// different one each day (backend/site_routes.py pick_video).
function VideoOfTheDay() {
  const [video, setVideo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/site/video-of-the-day')
      .then(res => (res.ok ? res.json() : null))
      .then(d => { if (!cancelled) setVideo(d?.video || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!video) return null;
  const date = new Date(`${video.date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <section className="site-video-feature" aria-labelledby="site-daily-video-title">
      <div className="site-wrap site-video-feature-cols">
        <div className="site-video-feature-player">
          <Markdown>{`![${video.title.replace(/[[\]]/g, '')}](${video.url})`}</Markdown>
        </div>
        <div className="site-video-feature-text">
          <span className="site-kicker">Video of the day</span>
          <h2 className="site-h2" id="site-daily-video-title">{video.title}</h2>
          <p>{video.venue}, {date}</p>
          <div className="site-actions">
            <a className="site-btn site-btn--ink" href={links.videos}>Browse all videos</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// The hero, the featured video and the "Anyone can have a go" panel are
// edited in Web Admin > Home page (stored as the 'home' page, a JSON document
// of fields; starting content in backend/site_content/home.json). If the
// backend can't be reached, the hero falls back to the club's name and the
// other two are left out.
const FALLBACK = {
  hero: {
    kicker: 'Motorcycles · Sidecars · Three-wheelers',
    title: 'National Hill Climb Association',
    text: 'Hill climbing is one of the friendliest, cheapest ways into motorcycle sport, and anyone can have a go.',
    primary: { label: 'Give it a go', href: links.giveItAGo },
    secondary: { label: 'How can I get involved?', href: links.introduction },
  },
};

function useHomeContent() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/site/pages/home')
      .then(res => (res.ok ? res.json() : null))
      .then(d => (d ? JSON.parse(d.body_md) : FALLBACK))
      .catch(() => FALLBACK)
      .then(c => { if (!cancelled) setContent(c); });
    return () => { cancelled = true; };
  }, []);
  return content;
}

function ButtonLink({ link, className }) {
  if (!link || !link.label || !link.href) return null;
  return <a className={className} href={link.href}>{link.label}</a>;
}

function VideoFeature({ video }) {
  if (!video || !video.show || !video.url) return null;
  return (
    <section className="site-video-feature" aria-labelledby="site-video-title">
      <div className="site-wrap site-video-feature-cols">
        <div className="site-video-feature-player">
          <Markdown>{`![${(video.title || 'Video').replace(/[[\]]/g, '')}](${video.url})`}</Markdown>
        </div>
        <div className="site-video-feature-text">
          {video.kicker && <span className="site-kicker">{video.kicker}</span>}
          {video.title && <h2 className="site-h2" id="site-video-title">{video.title}</h2>}
          {video.text && <p>{video.text}</p>}
        </div>
      </div>
    </section>
  );
}

export default function SiteHome() {
  const content = useHomeContent();
  const hero = content?.hero || {};
  const intro = content?.intro;
  useEffect(() => {
    const previous = document.title;
    document.title = 'National Hill Climb Association';
    return () => { document.title = previous; };
  }, []);

  return (
    <SiteLayout>
      <section className="site-hero">
        <img src={hero.image || HOME_PHOTOS.hero}
          alt={hero.image ? hero.imageAlt || '' : 'A rider on an orange and blue bike leaning into a bend'} />
        <div className="site-wrap">
          <div className="site-hero-panel">
            {content && (
              <>
                {hero.kicker && <span className="site-kicker">{hero.kicker}</span>}
                <h1>{hero.title}</h1>
                {hero.text && <p>{hero.text}</p>}
                <div className="site-actions">
                  <ButtonLink link={hero.primary} className="site-btn site-btn--orange" />
                  <ButtonLink link={hero.secondary} className="site-btn site-btn--ghost" />
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <Stripe />
      <NextEventBar />

      <section className="site-quick" aria-label="Quick links">
        <div className="site-wrap site-quick-grid">
          {QUICK_LINKS.map(l => (
            <a key={l.title} className="site-card-link" href={l.href}>
              <span className="site-card-kicker">{l.kicker}</span>
              <span className="site-card-title">{l.title}</span>
              <span className="site-card-text">{l.text}</span>
              <span className="site-card-cta">{l.cta} →</span>
            </a>
          ))}
        </div>
      </section>

      <VideoOfTheDay />

      <Memories />

      <VideoFeature video={content?.video} />

      {intro && (
        <section className="site-dark" aria-labelledby="site-intro-title">
          <div className="site-wrap site-intro">
            <div className="site-intro-photo">
              <img src={intro?.image || HOME_PHOTOS.intro} loading="lazy"
                alt={intro?.image ? intro.imageAlt || '' : 'A rider in red leathers taking a tight bend'} />
            </div>
            <div className="site-intro-text">
              {intro.kicker && <span className="site-kicker">{intro.kicker}</span>}
              <h2 className="site-h2" id="site-intro-title">{intro.title}</h2>
              {intro.text && <p>{intro.text}</p>}
              {intro.facts && intro.facts.some(f => f.value) && (
                <dl className="site-facts">
                  {intro.facts.filter(f => f.value).map(f => (
                    <div key={`${f.value}-${f.label}`}><dt>{f.label}</dt><dd>{f.value}</dd></div>
                  ))}
                </dl>
              )}
              {intro.small && <p className="site-small">{intro.small}</p>}
              <div className="site-actions">
                <ButtonLink link={intro.button} className="site-btn site-btn--orange" />
                <a className="site-text-link--light" href={links.faq}>Read the FAQ</a>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="site-classes" aria-labelledby="site-classes-title">
        <div className="site-wrap">
          <div className="site-section-head">
            <div>
              <span className="site-kicker">Championships</span>
              <h2 className="site-h2" id="site-classes-title">Find your class</h2>
            </div>
            <a className="site-more-link" href={links.classes}>All classes and rules →</a>
          </div>
          <div className="site-class-grid">
            {CLASSES.map(c => (
              <div key={c.name} className="site-class" style={{ '--chip': `var(--site-${c.chip})` }}>
                <h3>{c.name}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
